import { useMutation, useQuery, useQueryClient } from '@zg-rentals/react-query5';
import { user } from '@/src/lib/api/endpoints/user';
import type { InitialUserData, SavedSearchCreateBody, SavedSearchItem } from '@/src/lib/api/endpoints/user';

import { savedSearchKeys } from './queryKeys';
import { updateUserCacheFromAuthResponse, userKeys } from './useUserQuery';

const SAVED_SEARCH_TAG = 'savedSearches';

export const useSavedSearches = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: savedSearchKeys.list(),
    queryFn: async () => {
      const response = await user.getSavedSearches();
      return response.data;
    },
    enabled: options?.enabled,
    staleTime: 30_000,
    meta: {
      tags: [SAVED_SEARCH_TAG],
      context: { api: 'hotpads-api', details: 'fetch saved searches' },
    },
  });
};

export class SaveSearchError extends Error {
  status: string;
  constructor(message: string, status: string) {
    super(message);
    this.status = status;
  }
}

export const useCreateSavedSearch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: SavedSearchCreateBody) => {
      const response = await user.createSavedSearch(body);
      if (!response.success) {
        throw new SaveSearchError(response.message ?? 'Failed to save search', response.status ?? '');
      }
      return response;
    },
    meta: {
      invalidates: [SAVED_SEARCH_TAG],
      context: { api: 'hotpads-api', details: 'create saved search' },
    },
    onSuccess: async (response) => {
      const previousUser = queryClient.getQueryData<InitialUserData | null>(userKeys.info);
      if (response.loggedIn && !previousUser?.loggedIn) {
        try {
          const userInfo = await user.getUserInfo();
          updateUserCacheFromAuthResponse(queryClient, userInfo);
        } catch {
          // best-effort; auth cookie is set so page refresh will recover
        }
      }
    },
  });
};

export const useUpdateSavedSearch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: SavedSearchItem) => user.updateSavedSearch(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: savedSearchKeys.all });
    },
  });
};

export const useDeleteSavedSearch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (searchId: string) => user.deleteSavedSearch(searchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: savedSearchKeys.all });
    },
  });
};
